/**
 * The plan tiers, and how they are ordered and coloured.
 *
 * These mirror the `subscription_tier` enum and the backend's change-ladder. Hand-maintained copies
 * drift: a tier missing from an order map sorts at 0, below basic, so a move up to it reads as a
 * downgrade, and one missing from a class map renders with no styling at all.
 *
 * The class strings are literals on purpose. Tailwind scans source for class names, so a name built
 * at runtime is not in the stylesheet.
 */

export const PLAN_TIERS = ['basic', 'standard', 'professional', 'enterprise', 'custom'] as const;

export type PlanTier = (typeof PLAN_TIERS)[number];

/** Rungs, matching the backend ladder that decides upgrade from downgrade. */
export const TIER_ORDER: Record<PlanTier, number> = {
  basic: 1,
  standard: 2,
  professional: 3,
  enterprise: 4,
  custom: 5
};

/** Badge fill and text. */
export const TIER_BADGE_CLASSES: Record<PlanTier, string> = {
  basic: 'bg-blue-100 text-blue-700',
  standard: 'bg-emerald-100 text-emerald-700',
  professional: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
  custom: 'bg-gray-100 text-gray-700'
};

/** Badge fill, text and border, for a badge sitting on a white surface. */
export const TIER_BADGE_BORDER_CLASSES: Record<PlanTier, string> = {
  basic: 'bg-blue-100 text-blue-700 border-blue-200',
  standard: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  professional: 'bg-purple-100 text-purple-700 border-purple-200',
  enterprise: 'bg-amber-100 text-amber-700 border-amber-200',
  custom: 'bg-gray-100 text-gray-700 border-gray-200'
};

/** A solid dot, used where the tier is a marker beside a plan name. */
export const TIER_DOT_CLASSES: Record<PlanTier, string> = {
  basic: 'bg-blue-500',
  standard: 'bg-emerald-500',
  professional: 'bg-purple-500',
  enterprise: 'bg-amber-500',
  custom: 'bg-gray-500'
};

/** A card surface: soft fill and border. */
export const TIER_CARD_CLASSES: Record<PlanTier, string> = {
  basic: 'border-blue-200 bg-blue-50',
  standard: 'border-emerald-200 bg-emerald-50',
  professional: 'border-purple-200 bg-purple-50',
  enterprise: 'border-amber-200 bg-amber-50',
  custom: 'border-gray-200 bg-gray-50'
};

/** Falls back rather than answering undefined, which renders as an unstyled element. */
export function tierClasses(
  map: Record<PlanTier, string>,
  tier: string | null | undefined,
  fallback: string
): string {
  return tier && tier in map ? map[tier as PlanTier] : fallback;
}

/** Where a tier sits on the ladder. An unrecognised tier sorts below every named one. */
export function tierRank(tier: string | null | undefined): number {
  return tier && tier in TIER_ORDER ? TIER_ORDER[tier as PlanTier] : 0;
}
